"use client";

import { AlertTriangle, CheckCircle2, Clock, DollarSign } from "lucide-react";
import type { TechficientCommissionRow } from "@/app/_module/services/techficientService";

interface CommissionBadgeProps {
  commission: TechficientCommissionRow | null | undefined;
  /** Compact mode (no helper text) — for tight ledger rows. */
  compact?: boolean;
}

const fmtCurrency = (n: number | undefined | null) => {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const abs = Math.round(Math.abs(x));
  return `${x < 0 ? "-" : ""}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtDate = (s: string | undefined | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

export function CommissionBadge({ commission, compact }: CommissionBadgeProps) {
  if (!commission) return null;

  const { status, expectedAmount, advancedAmount, chargedBackAmount, daysUntilEarned, chargedBackReason } = commission;

  if (status === "advanced") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px]">
        <DollarSign className="h-3 w-3 text-emerald-600" />
        <span className="font-semibold text-emerald-800 tabular-nums">
          {fmtCurrency(advancedAmount ?? expectedAmount)}
        </span>
        <span className="text-emerald-600/70">advanced</span>
        {!compact && daysUntilEarned != null && (
          <span className="text-emerald-600/50 tabular-nums">
            · {daysUntilEarned}d
          </span>
        )}
      </span>
    );
  }

  if (status === "earned") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px]">
        <CheckCircle2 className="h-3 w-3 text-emerald-600" />
        <span className="font-semibold text-emerald-800 tabular-nums">
          {fmtCurrency(advancedAmount ?? expectedAmount)}
        </span>
        <span className="text-emerald-600/70">earned</span>
      </span>
    );
  }

  if (status === "charged_back") {
    const cbRaw = chargedBackAmount ?? advancedAmount;
    const cbNum = Number(cbRaw);
    const cbLabel =
      cbRaw == null || !Number.isFinite(cbNum) || cbNum === 0
        ? fmtCurrency(cbRaw)
        : `−${fmtCurrency(Math.abs(cbNum))}`;
    return (
      <div className="inline-flex flex-col gap-0.5">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-medium">
          <AlertTriangle className="h-3 w-3 text-rose-600" />
          <span className="text-rose-700">
            {cbLabel} chargeback
          </span>
        </div>
        {!compact && chargedBackReason && (
          <span className="text-[10px] text-rose-500/80 truncate max-w-[260px]">
            {chargedBackReason}
          </span>
        )}
      </div>
    );
  }

  // status === 'pending'
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] font-medium">
      <Clock className="h-3 w-3 text-amber-600" />
      <span className="text-amber-700">
        {fmtCurrency(expectedAmount)} projected
      </span>
    </div>
  );
}

export default CommissionBadge;
